/**
 * RUReader — Read-and-Understood flow
 *
 * Source: drafts/31_ux_design_system_spec.md §4.2 + §5 Flow 4.
 *
 * Critical gating per TRN-002:
 *   1. Sign button DISABLED until user scrolls to end.
 *   2. For Critical SOPs, comprehension quiz must be passed.
 *   3. Sign opens the SignatureConfirmationModal (READ_AND_UNDERSTOOD meaning).
 *
 * Dwell time is RECORDED as analytics (v3 A2) — NEVER a gate.
 *
 * Data: document body + quiz loaded via /document-versions/{id}/read-payload.
 * Quiz answer key stays server-side in the same payload (mock-api emits it
 * inline today; production must route grading through signature-svc so the
 * answer key is never exposed before submission).
 */

import { useEffect, useRef, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, SignatureConfirmationModal, StatusBadge } from '@edms/design-system';
import styles from './RUReader.module.css';
import { useCurrentUser, useReadPayload, useRequestSignatureChallenge } from '../../api/hooks';

interface RUReaderProps {
  /** Document-version ID being read. Defaults to a demo version so the screen
   *  renders standalone without a router. */
  versionId?: string;
}

export function RUReader({ versionId = 'd1-v3' }: RUReaderProps) {
  const { t } = useTranslation();
  const payloadQ = useReadPayload(versionId);
  const userQ = useCurrentUser();
  const challengeMutation = useRequestSignatureChallenge();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizAttempted, setQuizAttempted] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  // Dwell-time tracking (analytics only — NOT a gate, per v3 A2)
  const [dwellSeconds, setDwellSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDwellSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      if (!el) return;
      const reachedEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      if (reachedEnd && !scrolledToEnd) setScrolledToEnd(true);
    }
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrolledToEnd]);

  if (payloadQ.isLoading) return <p className={styles.dwell} role="status">Loading document…</p>;
  if (payloadQ.isError || !payloadQ.data) {
    return (
      <p className={styles.quizError} role="alert">
        Failed to load document {versionId}. {payloadQ.error?.message}
      </p>
    );
  }

  const doc = payloadQ.data;
  const challenge = challengeMutation.data;

  /**
   * TRN-002 invariant: grading is SERVER-SIDE. The client POSTs the chosen
   * option ids to /api/v1/document-versions/{id}/quiz/answers and reads the
   * authoritative passed/score response. The client NEVER inspects the
   * correct answer key (it's not in the payload — see hooks.ts QuizQuestion).
   */
  async function submitQuiz() {
    setQuizAttempted(true);
    try {
      const res = await fetch(apiUrl(`/api/v1/document-versions/${doc.id}/quiz/answers`), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({ chosen_option_ids: quizAnswers }),
      });
      if (res.status === 200) {
        const body = await res.json();
        setQuizPassed(Boolean(body.passed));
      } else {
        setQuizPassed(false);
      }
    } catch {
      setQuizPassed(false);
    }
  }

  const canSign = scrolledToEnd && (doc.is_critical_sop ? quizPassed : true);

  function onSign() {
    challengeMutation.mutate(
      {
        target_type: 'training_task',
        target_id: doc.id,
        signature_meaning: 'READ_AND_UNDERSTOOD',
      },
      { onSuccess: () => setShowSignModal(true) },
    );
  }

  return (
    <div className={styles.ru}>
      <div className={styles.layout}>
        <main className={styles.viewer} ref={scrollRef}>
          <div className={styles.watermark} aria-hidden="true">
            CONTROLLED — {userQ.data?.full_name ?? '…'} — {new Date().toISOString()}
          </div>

          <header className={styles.docHeader}>
            <div className={styles.docMetaRow}>
              <span className={styles.docNumber}>{doc.doc_number}</span>
              <StatusBadge state="EFFECTIVE" />
            </div>
            <h1 className={styles.title}>{doc.title}</h1>
            <p className={styles.version}>
              {doc.version} — Effective {doc.effective_date}
            </p>
          </header>

          {doc.body_paragraphs.map((p, i) => (
            <p key={i} className={styles.body}>{p}</p>
          ))}

          <div className={styles.endMarker}>— End of document —</div>
        </main>

        <aside className={styles.sidebar}>
          <Card>
            <CardBody>
              <h2 className={styles.sidebarHeading}>{t('reader.progressHeading', { defaultValue: 'Progress' })}</h2>
              <ul className={styles.gateList}>
                <li className={scrolledToEnd ? styles.gatePassed : styles.gatePending}>
                  {scrolledToEnd ? '✓' : '○'} {t('reader.scrollGate', { defaultValue: 'Scroll to end of document' })}
                </li>
                {doc.is_critical_sop && (
                  <li
                    className={
                      quizPassed
                        ? styles.gatePassed
                        : quizAttempted
                          ? styles.gateFailed
                          : styles.gatePending
                    }
                  >
                    {quizPassed ? '✓' : quizAttempted ? '✕' : '○'}{' '}
                    {t('reader.quizGate', { defaultValue: 'Pass comprehension quiz' })}
                  </li>
                )}
              </ul>
              <p className={styles.dwell}>
                {t('reader.dwellAnalytics', { defaultValue: 'Reading time' })}: {dwellSeconds}s{' '}
                <small>(analytics only)</small>
              </p>
            </CardBody>
          </Card>

          {doc.is_critical_sop && scrolledToEnd && !quizPassed && (
            <Card>
              <CardBody>
                <h2 className={styles.sidebarHeading}>
                  {t('reader.quizHeading', { defaultValue: 'Comprehension Check' })}
                </h2>
                {doc.quiz.map((q) => (
                  <fieldset key={q.id} className={styles.quizQuestion}>
                    <legend className={styles.quizLegend}>{q.prompt}</legend>
                    {q.options.map((o) => (
                      <label key={o.id} className={styles.quizOption}>
                        <input
                          type="radio"
                          name={q.id}
                          value={o.id}
                          checked={quizAnswers[q.id] === o.id}
                          onChange={() => setQuizAnswers((p) => ({ ...p, [q.id]: o.id }))}
                        />
                        {o.text}
                      </label>
                    ))}
                  </fieldset>
                ))}
                <Button variant="primary" onClick={submitQuiz} isFullWidth>
                  {t('reader.submitQuiz', { defaultValue: 'Submit answers' })}
                </Button>
                {quizAttempted && !quizPassed && (
                  <p role="alert" className={styles.quizError}>
                    {t('reader.quizRetry', {
                      defaultValue:
                        'One or more answers were incorrect. Please review the procedure and try again.',
                    })}
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          <Button
            variant="primary"
            size="lg"
            isFullWidth
            disabled={!canSign}
            isLoading={challengeMutation.isPending}
            onClick={onSign}
          >
            {canSign
              ? `🔓 ${t('reader.sign', { defaultValue: 'Sign Read & Understood' })}`
              : `🔒 ${t('reader.signLocked', { defaultValue: 'Complete gates to sign' })}`}
          </Button>
        </aside>
      </div>

      {challenge && (
        <SignatureConfirmationModal
          isOpen={showSignModal}
          signerFullName={userQ.data?.full_name ?? ''}
          signerJobTitle={userQ.data?.role ?? ''}
          expectedInitials={userQ.data?.initials ?? ''}
          signatureMeaning="READ_AND_UNDERSTOOD"
          target={`${doc.doc_number} ${doc.version}`}
          utcTimestamp={challenge.challenge_expires_at}
          attestationText={challenge.attestation_text}
          attestationTextVersion={challenge.attestation_version}
          onCancel={() => setShowSignModal(false)}
          onConfirmIdentity={() => {
            setShowSignModal(false);
          }}
        />
      )}
    </div>
  );
}
